import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, User, Menu, X, Instagram, Facebook, Youtube, MapPin, ShoppingBag, Home as HomeIcon, Users as UsersIcon, Plus, Camera, Video, FileText, Star, Trophy, Sparkles, MessageSquare, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useOutlet } from "../context/OutletContext";
import { API, dashboardFor } from "../lib/api";

const NAV = [
  ["Discover", "/discover"], ["Beauty", "/category/cosmetics"], ["Fashion", "/category/ladies-fashion"],
  ["Community", "/sakhi-wall"], ["Sakhi Chat", "/sakhi-chat"], ["Creators", "/creators"], ["Club", "/club"],
];

export function ReferralTracker() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      localStorage.setItem("svk_ref", ref);
      API.post(`/affiliate/track?ref=${encodeURIComponent(ref)}`).catch(() => {});
    }
    const utm = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
      const v = params.get(k);
      if (v) utm[k] = v;
    }
    if (Object.keys(utm).length) localStorage.setItem("svk_utm", JSON.stringify(utm));
  }, []);
  return null;
}

export function OutletPicker() {
  const { outlets, outlet, location, resolve, clearLocation, setOutlet } = useOutlet();
  const [open, setOpen] = useState(false);
  const [pincode, setPincode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const doPincode = async () => {
    if (!/^[0-9]{6}$/.test(pincode.trim())) { setMsg("Enter a valid 6-digit pincode"); return; }
    setBusy(true); setMsg("");
    try {
      const r = await resolve({ pincode: pincode.trim() });
      if (r.serviceable) { setMsg(""); setOpen(false); }
      else setMsg("Sorry, Suvikka doesn't deliver to this pincode yet.");
    } finally { setBusy(false); }
  };

  const doGeo = () => {
    if (!navigator.geolocation) { setMsg("Location not supported on this device"); return; }
    setBusy(true); setMsg("");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        if (r.serviceable) setOpen(false);
        else setMsg("Suvikka doesn't serve your location yet.");
      } finally { setBusy(false); }
    }, () => { setBusy(false); setMsg("Could not get your location. Enter a pincode instead."); }, { timeout: 8000 });
  };

  const label = outlet ? outlet.name : (location?.label ? `${location.label} — unavailable` : "Select location");

  return (
    <div className="relative" data-testid="outlet-picker">
      <button data-testid="outlet-picker-button" onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm font-medium text-stone-700 hover:text-[#6A1B29] max-w-[200px]">
        <MapPin className="w-4 h-4 text-[#6A1B29] shrink-0" />
        <span className="truncate text-left">
          <span className="block text-[10px] text-stone-400 leading-none">Deliver to</span>
          <span className="block truncate leading-tight">{label}</span>
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white border border-stone-200 rounded-xl shadow-xl p-4 z-[70]" data-testid="outlet-picker-menu">
          <p className="text-sm font-semibold text-stone-900 mb-2">Choose your delivery location</p>
          <button onClick={doGeo} disabled={busy} className="w-full flex items-center justify-center gap-2 border border-stone-300 rounded-lg py-2 text-sm font-medium hover:border-[#6A1B29] mb-3" data-testid="loc-use-current">
            <MapPin className="w-4 h-4 text-[#6A1B29]" /> Use my current location
          </button>
          <div className="flex gap-2 mb-1">
            <input value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="Enter 6-digit pincode" maxLength={6}
              className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#6A1B29]" data-testid="loc-pincode-input" />
            <button onClick={doPincode} disabled={busy} className="bg-stone-900 text-white text-sm font-semibold px-4 rounded-lg" data-testid="loc-pincode-go">Go</button>
          </div>
          {msg && <p className="text-xs text-red-600 mt-1" data-testid="loc-msg">{msg}</p>}
          {outlets.length > 0 && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <p className="text-xs text-stone-400 uppercase tracking-wider mb-1.5">Or pick an outlet directly</p>
              <div className="max-h-40 overflow-y-auto">
                {outlets.map((o) => (
                  <button key={o.id} onClick={() => { setOutlet(o); setOpen(false); }} data-testid={`outlet-option-${o.id.slice(0, 6)}`}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-stone-50 ${outlet?.id === o.id ? "text-[#6A1B29] font-semibold" : "text-stone-700"}`}>
                    {o.name}<span className="block text-xs text-stone-400">{o.city}, {o.state}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(outlet || location) && (
            <button onClick={() => { clearLocation(); setOpen(false); }} className="mt-3 text-xs font-semibold text-stone-500 hover:text-[#6A1B29]" data-testid="loc-clear">
              Clear · browse full catalogue
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/products?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF7F2]/90 border-b border-[#EADBD8] overflow-x-clip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-16 gap-3">
          <Link to="/" data-testid="nav-brand-logo" className="flex flex-col leading-none shrink-0">
            <span className="font-display text-xl lg:text-2xl font-bold tracking-[0.16em] text-[#6A1B29]">SUVIKKA</span>
            <span className="text-[8px] tracking-[0.3em] text-[#755C62] italic font-medium">Everything She Loves</span>
          </Link>
          <nav className="hidden xl:flex items-center gap-2.5 text-[13px] font-medium text-[#231518] whitespace-nowrap min-w-0 mr-auto pl-4">
            {NAV.map(([label, to]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `hover:text-[#6A1B29] transition-colors ${isActive ? "text-[#6A1B29] font-semibold" : ""}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <div className="hidden 2xl:block"><OutletPicker /></div>
            <form onSubmit={doSearch} className="hidden lg:flex items-center border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Search className="w-4 h-4 text-stone-400 shrink-0" />
              <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search…" className="ml-2 text-[13px] outline-none w-20 2xl:w-32 bg-transparent" />
            </form>
            <Link to="/cart" data-testid="nav-cart-button" className="relative p-2 text-stone-700 hover:text-[#6A1B29] shrink-0">
              <ShoppingBag className="w-5 h-5" />
              {cart?.count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#6A1B29] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count-badge">{cart.count}</span>}
            </Link>
            <Link to="/wallet" data-testid="nav-wallet-button" className="hidden 2xl:flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-stone-700 hover:text-[#6A1B29]">Wallet</Link>
            <Link to="/suvikka-sakhi" data-testid="nav-club-button" className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white bg-[#6A1B29] hover:bg-[#501325] px-3 py-2 rounded-full shrink-0">💗 Join Sakhi</Link>
            {user ? (
              <>
                <Link to={dashboardFor(user.role)} data-testid="nav-dashboard-link"
                  className="hidden 2xl:flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-stone-700 hover:text-[#6A1B29] whitespace-nowrap">
                  <User className="w-3.5 h-3.5" /> {user.role === "admin" ? "Control Room" : user.role === "staff" ? "Growth Desk" : user.role === "affiliate" ? "Partner Hub" : "Style Hub"}
                </Link>
                <button data-testid="nav-logout-button" onClick={async () => { await logout(); nav("/"); }}
                  className="hidden lg:inline-block text-xs font-semibold uppercase tracking-wider text-stone-500 hover:text-red-500 whitespace-nowrap shrink-0">Logout</button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-button" className="hidden lg:inline-block text-xs font-semibold uppercase tracking-wider text-stone-700 hover:text-[#6A1B29] whitespace-nowrap shrink-0">Login</Link>
            )}
            <button data-testid="nav-mobile-menu-button" className="xl:hidden p-1.5 shrink-0" onClick={() => setOpen(!open)}>
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="xl:hidden border-t border-stone-200 bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          <div className="mb-3"><OutletPicker /></div>
          <form onSubmit={(e) => { doSearch(e); setOpen(false); }} className="flex items-center border border-stone-300 rounded-full px-3 py-2 mb-3">
            <Search className="w-4 h-4 text-stone-400" />
            <input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, SKU..." className="ml-2 text-sm outline-none flex-1" />
          </form>
          {NAV.map(([label, to]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className="block py-2 text-stone-800 font-medium border-b border-stone-100">{label}</Link>
          ))}
          <Link to="/wallet" onClick={() => setOpen(false)} data-testid="mobile-nav-wallet" className="block py-2 text-stone-800 font-medium border-b border-stone-100">Wallet</Link>
          <Link to="/club" onClick={() => setOpen(false)} data-testid="mobile-nav-club" className="block py-2 text-[#6A1B29] font-bold">💗 My Club</Link>
          {user && <Link to={dashboardFor(user.role)} onClick={() => setOpen(false)} data-testid="mobile-nav-dashboard" className="block py-2 text-[#6A1B29] font-semibold">My Dashboard</Link>}
        </div>
      )}
    </header>
  );
}

export function MobileCTA() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const me = user ? dashboardFor(user) : "/login";
  const active = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));
  const navItems = [["Home", "/", HomeIcon], ["Chat", "/sakhi-chat", MessageCircle], null, ["Community", "/sakhi-wall", UsersIcon], ["Me", me, User]];
  const createOpts = [
    ["Post a Look", "/share-look", Camera], ["Upload Photo / Video", "/share-look", Video],
    ["Join Sakhi Chat", "/sakhi-chat", MessageCircle],
    ["Share Story", "/community/share", FileText], ["Create Review", "/community/share", Star],
    ["Ask Sakhis", "/ask-sakhis", MessageSquare],
    ["Join Challenge", "/suvikka-sakhi", Trophy],
  ];
  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="create-overlay">
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 w-[min(92vw,380px)] bg-white rounded-3xl p-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="create-sheet">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6A1B29] px-3 pt-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Create</p>
            <div className="mt-2">
              {createOpts.map(([l, to, Icon]) => (
                <Link key={l} to={to} onClick={() => setOpen(false)} data-testid={`create-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-[#FFF0F4] transition-colors">
                  <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FFF0F4] text-[#6A1B29]"><Icon className="w-4 h-4" /></span>
                  <span className="text-sm font-medium text-stone-800">{l}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      <button onClick={() => setOpen(true)} data-testid="create-fab-desktop"
        className="hidden md:flex fixed bottom-6 right-6 z-[85] w-14 h-14 rounded-full bg-[#6A1B29] hover:bg-[#501325] text-white shadow-2xl items-center justify-center transition-transform hover:scale-110">
        <Plus className="w-6 h-6" />
      </button>
      <nav className="fixed bottom-0 inset-x-0 md:hidden z-50 bg-white/95 backdrop-blur-lg border-t border-[#EADBD8]" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5 items-center">
          {navItems.map((it, i) => it === null ? (
            <div key="create" className="flex justify-center">
              <button onClick={() => setOpen(true)} data-testid="create-fab-mobile" className="-mt-6 w-14 h-14 rounded-full bg-[#6A1B29] text-white shadow-lg grid place-items-center active:scale-95 transition-transform"><Plus className="w-6 h-6" /></button>
            </div>
          ) : (
            <Link key={it[0]} to={it[1]} data-testid={`bottomnav-${it[0].toLowerCase()}`} className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${active(it[1]) ? "text-[#6A1B29]" : "text-[#9E838B]"}`}>
              {(() => { const Ic = it[2]; return <Ic className="w-5 h-5" />; })()} {it[0]}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  if (!show || !deferred) return null;
  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null); setShow(false);
  };
  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-[80] bg-stone-900 text-white rounded-full shadow-2xl pl-4 pr-2 py-2 flex items-center gap-3" data-testid="pwa-install-banner">
      <img src="/icons/icon-192.png" alt="" className="w-7 h-7 rounded-lg" />
      <span className="text-sm font-medium whitespace-nowrap">Install the SUVIKKA app</span>
      <button onClick={install} className="text-xs font-semibold bg-[#6A1B29] hover:bg-[#501325] px-4 py-2 rounded-full" data-testid="pwa-install-btn">Add to Home Screen</button>
      <button onClick={() => setShow(false)} className="text-stone-400 hover:text-white p-1" data-testid="pwa-install-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

export function Footer() {
  const [socials, setSocials] = useState({});
  useEffect(() => { API.get("/settings/public").then((r) => setSocials(r.data)).catch(() => {}); }, []);
  const cols = [
    ["SUVIKKA", [["About", "/about"], ["Contact", "/contact"], ["Trust Center", "/trust"]]],
    ["SUVIKKA SAKHI", [["The Community", "/suvikka-sakhi"], ["Community Feed", "/community"], ["Challenges", "/community"], ["Sakhi Stories", "/community"], ["Join Suvikka Sakhi", "/signup"]]],
    ["SHOP & EARN", [["Products", "/products"], ["Suvikka Club", "/club"], ["My Wallet", "/wallet"], ["Product Affiliate", "/affiliate"]]],
    ["SUPPORT", [["Trust Center", "/trust"], ["FAQs", "/trust"], ["Contact", "/contact"], ["Terms", "/affiliate-terms"]]],
  ];
  return (
    <footer className="bg-[#2A1418] text-stone-300 mt-0" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2 md:col-span-1">
          <p className="font-display text-2xl tracking-[0.18em] text-white">SUVIKKA</p>
          <p className="text-xs tracking-[0.25em] text-[#6A1B29] uppercase mt-1 mb-4">Everything She Loves</p>
          <p className="text-sm text-stone-400">A women's community for fashion, beauty & lifestyle — built by Sakhis, for Sakhis.</p>
          <p className="text-sm text-stone-300 mt-3">Contact Us: <a href={`tel:${socials.contact_phone || "7633993332"}`} className="text-white font-semibold hover:text-[#6A1B29]" data-testid="footer-contact-phone">{socials.contact_phone || "7633993332"}</a></p>
          <div className="flex gap-3 mt-4">
            {socials.social_instagram && <a href={socials.social_instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram"><Instagram className="w-5 h-5 hover:text-[#6A1B29]" /></a>}
            {socials.social_facebook && <a href={socials.social_facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook"><Facebook className="w-5 h-5 hover:text-[#6A1B29]" /></a>}
            {socials.social_youtube && <a href={socials.social_youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube"><Youtube className="w-5 h-5 hover:text-[#6A1B29]" /></a>}
          </div>
        </div>
        {cols.map(([title, links]) => (
          <div key={title}>
            <p className="text-xs font-semibold tracking-[0.2em] text-stone-500 uppercase mb-4">{title}</p>
            <ul className="space-y-2.5 text-sm">
              {links.map(([label, to]) => (
                <li key={label}><Link to={to} data-testid={`footer-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-white transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-stone-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row gap-2 justify-between text-xs text-stone-500">
          <p>© {new Date().getFullYear()} Suvikka. All rights reserved. suvikka.com</p>
          <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> A Pan-India Community of Sakhis</p>
        </div>
      </div>
      <div className="h-16 md:hidden" />
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#231518]">
      <Header />
      <main className="pb-20 md:pb-0">{children}</main>
      <InstallPrompt />
      <Footer />
      <MobileCTA />
    </div>
  );
}
